<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractServiceCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const isServiceStale = (...args) => {
    const [service] = args;
    return (
      service.valid_to && $fn.value.parseISODate(service.valid_to) < today.value
    );
  };

  const today = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    today: today.value,
    ux: ux.value,
  }));

  const $$load_today = async () => {
    return new Date();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'today' }, { name: 'ux' }],
    loaders: { today: $$load_today },
    ux: {},
    dataRefs: { today, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_7315092382398562_reloadComponentData_99dndcf1k =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_7z04pgnzh =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_99dndcf1k,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_7z04pgnzh],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_7z04pgnzh,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_7z04pgnzh = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_7z04pgnzh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox alignItems="center" :gap="2" class="mb-1">
      <ItpText
        :text="item.name"
        :strikethrough="isServiceStale(item)"
        class="fw-bold"
      >
      </ItpText>
      <ItpLink
        :icon="$config.externalLinkIcon"
        :to="{ name: 'app.product', params: { id: item.service.id } }"
      >
      </ItpLink>
      <ItpIcon :title="gettext('Locked')" name="fa-lock" v-if="item.locked">
      </ItpIcon>
      <UcProductInvoiceGroupLabel
        :value="item.invoice_group_id"
        v-if="item.invoice_group_id"
      >
      </UcProductInvoiceGroupLabel>
      <div>
        {{ date(item.valid_from) }} <span v-if="item.valid_to">-</span>
        {{ date(item.valid_to) }}
      </div>
    </ItpBox>
    <ItpBox direction="column" :gap="2">
      <ItpBox
        alignItems="center"
        :gap="2"
        v-if="
          item.invoice_notes ||
          (item.transaction_notes && item.transaction_notes.length)
        "
      >
        <ItpIcon icon="uil:notes"> </ItpIcon>
        <ItpText :text="item.invoice_notes" pre> </ItpText>
        <TransactionNotesDisplay
          v-model="item.transaction_notes"
          :value="item.transaction_notes"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.transaction_notes =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          v-if="item.transaction_notes && item.transaction_notes.length"
        >
        </TransactionNotesDisplay>
      </ItpBox>
      <ItpBox alignItems="center" :gap="2" v-if="item.intermediated_service">
        <ItpIcon icon="mingcute:arrows-right-fill"> </ItpIcon>
        <ItpText :text="gettext('Intermediated service')"> </ItpText>
      </ItpBox>
      <ItpBox alignItems="center" :gap="2" v-if="item.billing_category">
        <ItpIcon icon="ph:circles-three-fill"> </ItpIcon>
        <ItpText :text="item.billing_category"> </ItpText>
      </ItpBox>
      <ItpBox alignItems="start" :gap="2">
        <ItpIcon icon="uil:cog" class="pt-1"> </ItpIcon>
        <ItpBox wrap-items :gap="1">
          <UcBillingFrequencyDisplay
            :value="item.billing_frequency"
            v-if="item.billing_frequency"
          >
          </UcBillingFrequencyDisplay>
          <UcBillingDateRuleDisplay
            :value="item.billing_date_rule"
            v-if="item.billing_date_rule"
          >
          </UcBillingDateRuleDisplay>
          <UcDueDateRuleDisplay
            :value="item.due_date_rule"
            v-if="item.due_date_rule"
          >
          </UcDueDateRuleDisplay>
          <UcCurrencyBadge
            :value="item.billing_currency"
            v-if="item.billing_currency"
          >
          </UcCurrencyBadge>
          <UcExchangeRateDateRuleDisplay
            :value="item.exchange_rate_date_rule"
            :source="item.exchange_rate_source"
            v-if="item.exchange_rate_date_rule"
          >
          </UcExchangeRateDateRuleDisplay>
        </ItpBox>
      </ItpBox>
      <ItpBox alignItems="center" :gap="2" v-if="item.description">
        <ItpIcon icon="material-symbols:info-outline"> </ItpIcon>
        <ItpText :text="item.description" pre> </ItpText>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
